import type { Sensor } from "@sitescene/types";

import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@/api/query-keys";
import { sensorsApi } from "@/api/sensors";
import { noRetryOn4xx, useQueryErrorToast } from "@/hooks/useQueryErrorToast";
import { useAuth } from "@/providers/auth-provider";

/**
 * Hook for fetching a single sensor by ID
 */
export function useGetSensor(
	sensorId: string | undefined,
	options?: { silent?: boolean },
) {
	const {
		data: sensorData,
		error: sensorError,
		isError: isSensorError,
		isLoading: isLoadingSensor,
		refetch: refetchSensor,
	} = useQuery<Sensor>({
		enabled: !!sensorId,
		queryFn: () => {
			if (!sensorId) throw new Error("Sensor ID is required");
			return sensorsApi.getSensor(sensorId);
		},
		queryKey: queryKeys.sensors.detail(sensorId!),
		retry: noRetryOn4xx,
		staleTime: 2 * 60 * 1000,
	});

	useQueryErrorToast({
		error: sensorError,
		isError: isSensorError && !options?.silent,
		title: "Failed to load sensor",
	});

	return {
		isLoadingSensor,
		isSensorError,
		refetchSensor,
		sensorData,
		sensorError,
	};
}

/**
 * Hook for fetching all sensors
 *
 * Uses centralized query keys and pure API functions.
 */
export function useGetSensors(options?: { enabled?: boolean }) {
	const { isAuthenticated, isLoadingAuth } = useAuth();
	const enabled = options?.enabled ?? true;

	const {
		data: sensorsData,
		error: sensorsError,
		isError: isSensorsError,
		isLoading: isLoadingSensors,
		refetch: refetchSensors,
	} = useQuery<Sensor[]>({
		enabled: isAuthenticated && !isLoadingAuth && enabled,
		queryFn: () => sensorsApi.getSensors(),
		queryKey: queryKeys.sensors.lists(),
		staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
	});

	return {
		isLoadingSensors,
		isSensorsError,
		refetchSensors,
		sensorsData,
		sensorsError,
	};
}

/**
 * Hook for fetching sensors by location ID
 */
export function useGetSensorsByLocation(locationId: string | undefined) {
	const {
		data: locationSensorsData,
		error: locationSensorError,
		isError: isLocationSensorError,
		isLoading: isLoadingLocationSensors,
		isSuccess: isLocationSensorsSuccess,
		refetch: refetchLocationSensors,
	} = useQuery<Sensor[]>({
		enabled: !!locationId,
		queryFn: () => {
			if (!locationId) throw new Error("Location ID is required");
			return sensorsApi.getSensorsByLocation(locationId);
		},
		queryKey: queryKeys.sensors.byLocation(locationId!),
		staleTime: 2 * 60 * 1000,
	});

	return {
		isLoadingLocationSensors,
		isLocationSensorError,
		isLocationSensorsSuccess,
		locationSensorError,
		locationSensorsData,
		refetchLocationSensors,
	};
}

/**
 * Hook for fetching sensors by multiple location IDs
 */
export function useGetSensorsByLocations(locationIds: string[]) {
	const {
		data: locationsSensorsData,
		error: locationsSensorsError,
		isError: isLocationsSensorsError,
		isLoading: isLoadingLocationsSensors,
		refetch: refetchLocationsSensors,
	} = useQuery<Sensor[]>({
		enabled: locationIds.length > 0,
		queryFn: () => sensorsApi.getSensorsByLocations(locationIds),
		queryKey: queryKeys.sensors.byLocations(locationIds),
		staleTime: 2 * 60 * 1000,
	});

	return {
		isLoadingLocationsSensors,
		isLocationsSensorsError,
		locationsSensorsData,
		locationsSensorsError,
		refetchLocationsSensors,
	};
}
